import Statistic from "common/Statistic/Statistic";
import styled, { useTheme } from "styled-components";
import { ReactNode, useContext } from "react";
import { css } from "styled-components";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import {
  DISPLAY_CURRENCIES,
  DOLLAR_COINS,
  ICON_TYPES,
} from "@shared/common/constants";
import PriceStatistic from "common/Statistic/PriceStatistic";
import { TradeDetailsContext } from "@shared/features/trades/TradeDetails.context";
import { TradeAmountData } from "@shared/features/trades/EditTrade/types";
import PreviousValuePopover from "features/trades/EditTrade/PreviosValuePopover";
import Flex from "common/Boxes/Flex";
import TextWithPrecision from "common/Text/TextWithPrecision";
import usePricesSocket from "@shared/services/socket/usePricesSocket";
import useMedia from "utils/hooks/useMedia";
import { useTradeStatistics } from "@shared/features/trades/common/hooks";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  directionColor,
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import { LanguageNameSpaces } from "@shared/types";
import StatStrip, { StatStripItem } from "common/NewLook/StatStrip.tsx";
import StatAmount from "common/NewLook/StatAmount.tsx";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";

interface TradeStatisticsProps {
  amountData?: TradeAmountData;
}

const TradeStatistics = ({ amountData }: TradeStatisticsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const {
    pricePrecision,
    symbol,
    exchange,
    statsData,
    ordersData,
    isBotDrawer,
  } = useContext(TradeDetailsContext)!;
  const {
    currentPrice,
    displayCurrencyAmountPrecision,
    totalAmount,
    fulfilledAmount,
    isGridBotTrade,
    amountPrecision,
    pnlText,
  } = useTradeStatistics(amountData?.selected);
  const isTwoRowsLayout = useMedia([`(max-width: 650px)`], [true], false);
  const theme = useTheme();
  const { socketPrice } = usePricesSocket(symbol, exchange);
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();

  const amountChanged = amountData?.original !== amountData?.selected;

  if (!statsData) return null;

  if (theme.isNewLook) {
    // New look: flat key figures, no coin icons ($ for dollar quotes).
    const tokens = getNewLookTokens(theme);
    const coin = statsData.main_coin;
    // Prices are in the quote coin; main_coin is the base coin on inverse
    // pairs, so it only fits the amounts.
    const priceCoin = ordersData?.data?.price_coin ?? coin;
    // Dollar amounts read in cents; the symbol's amount precision (often 6+
    // digits) is for the order size, not for reading "$39.89".
    const shownAmountPrecision = DOLLAR_COINS.includes(coin.toUpperCase())
      ? getDisplayCurrencyAmountPrecision(DISPLAY_CURRENCIES.USD)
      : amountPrecision;
    const amount = (text?: number | string) => (
      <StatAmount text={text} coin={coin} precision={shownAmountPrecision} />
    );
    const total = (
      <PreviousValuePopover
        popupsContainerId={CONTAINER_ID}
        valueName={t("statistics.total_amount")}
        valueChanged={amountChanged}
        color={isTwoRowsLayout ? tokens.ink : tokens.muted}
        previousValue={amount(amountData?.original)}
        value={amount(totalAmount)}
        alignBottom={true}
      />
    );
    const items: StatStripItem[] = [];
    if (!isGridBotTrade && isTwoRowsLayout) {
      items.push(
        {
          key: "fulfilled",
          label: t("statistics.fulfilled_amount"),
          value: amount(fulfilledAmount),
        },
        { key: "total", label: t("statistics.total_amount"), value: total }
      );
    } else if (!isGridBotTrade) {
      items.push({
        key: "fulfilled",
        label: t("statistics.fulfilled_total"),
        value: (
          <>
            {amount(fulfilledAmount)}
            <OfTotal>/{total}</OfTotal>
          </>
        ),
      });
    }
    items.push(
      {
        key: "pnl",
        label: isBotDrawer ? t("statistics.active_trade_pnl") : "PnL",
        value: (
          <DirectionValue
            style={{
              color: directionColor(tokens, Number(pnlText)),
            }}
          >
            <TextWithPrecisionAndIcon
              precision={displayCurrencyAmountPrecision}
              text={pnlText}
              showPlus={true}
            />
          </DirectionValue>
        ),
      },
      {
        key: "price",
        label: t("statistics.current_price"),
        value: (
          <StatAmount
            text={socketPrice.value || currentPrice}
            coin={priceCoin}
            precision={pricePrecision}
          />
        ),
      }
    );
    return (
      <div id={CONTAINER_ID} style={{ width: "100%" }}>
        <StatStrip variant="flat" items={items} />
      </div>
    );
  }

  const priceStatistic = (
    <StyledPriceStatistic
      key="currentPrice"
      currentPrice={currentPrice}
      coin={statsData.main_coin}
      exchange={exchange}
      symbol={symbol}
      precision={pricePrecision}
    />
  );

  const getAmountStatistics = () => {
    if (isGridBotTrade) return [];
    const fulfilledAmountComponent = (
      <TextWithPrecisionAndIcon
        text={fulfilledAmount}
        precision={amountPrecision}
        iconType={ICON_TYPES.COIN}
        coinOrExchangeName={statsData?.main_coin}
      />
    );
    const originalTotalAmount = (
      <TextWithPrecisionAndIcon
        text={amountData?.original}
        precision={amountPrecision}
        iconType={ICON_TYPES.COIN}
        coinOrExchangeName={statsData?.main_coin}
      />
    );
    if (isTwoRowsLayout) {
      return [
        <StyledStatistic
          key="fulfilledAmount"
          title={t("statistics.fulfilled_amount")}
          value={fulfilledAmountComponent}
          $isBordered={true}
        />,
        <StyledStatistic
          key="totalAmount"
          title={t("statistics.total_amount")}
          value={
            <PreviousValuePopover
              popupsContainerId={CONTAINER_ID}
              valueName={t("statistics.total_amount")}
              valueChanged={amountChanged}
              previousValue={originalTotalAmount}
              value={
                <TextWithPrecisionAndIcon
                  text={totalAmount}
                  precision={amountPrecision}
                  iconType={ICON_TYPES.COIN}
                  coinOrExchangeName={statsData?.main_coin}
                />
              }
              alignBottom={true}
            />
          }
        />,
      ];
    }
    return [
      <StyledStatistic
        key="amount"
        title={t("statistics.fulfilled_total")}
        $isBordered={true}
        value={
          <PreviousValuePopover
            popupsContainerId={CONTAINER_ID}
            valueName={t("statistics.total_amount")}
            valueChanged={amountChanged}
            previousValue={originalTotalAmount}
            value={
              <Flex>
                {fulfilledAmountComponent}
                <span style={{ margin: "0 2px" }}>/</span>
                <TextWithPrecision
                  text={totalAmount}
                  precision={amountPrecision}
                />
              </Flex>
            }
            alignBottom={true}
          />
        }
      />,
    ];
  };

  const pnlStatistic = (
    <StyledStatistic
      key="pnl"
      title={isBotDrawer ? t("statistics.active_trade_pnl") : "PnL"}
      value={
        theme.isNewLook ? (
          // New look: PnL reads by direction, currency sign included.
          <DirectionValue
            style={{
              color: directionColor(getNewLookTokens(theme), Number(pnlText)),
            }}
          >
            <TextWithPrecisionAndIcon
              precision={displayCurrencyAmountPrecision}
              text={pnlText}
            />
          </DirectionValue>
        ) : (
          <TextWithPrecisionAndIcon
            precision={displayCurrencyAmountPrecision}
            text={pnlText}
          />
        )
      }
      $isBordered={true}
    />
  );

  const statistics: ReactNode[] = getAmountStatistics().concat([
    pnlStatistic,
    priceStatistic,
  ]);

  if (isTwoRowsLayout && theme.isNewLook) {
    return <GridWrapper id={CONTAINER_ID}>{statistics}</GridWrapper>;
  }

  if (isTwoRowsLayout) {
    return (
      <div id={CONTAINER_ID} style={{ width: "100%" }}>
        <Wrapper>{statistics.slice(0, 2)}</Wrapper>
        {statistics.length > 2 && <Wrapper>{statistics.slice(-2)}</Wrapper>}
      </div>
    );
  }

  return <Wrapper id={CONTAINER_ID}>{statistics}</Wrapper>;
};

export default TradeStatistics;

const CONTAINER_ID = "tradeStatistic";

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-around;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* A stat strip: label over figure, hairlines between the figures. */
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: minmax(0, 1fr);
      overflow: hidden;
      border-radius: ${NEW_LOOK_RADIUS.panel}px;
      background: ${getNewLookTokens(p.theme).panel};
      border: ${p.theme.borders.panel};
      box-shadow: ${p.theme.shadows.panel};
      & + & {
        margin-top: 8px;
      }
    `}
`;
const GridWrapper = styled(Wrapper)`
  display: grid;
  grid-auto-flow: row;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;
const OfTotal = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  && {
    font-size: 15px;
    font-weight: 400;
  }
  * {
    font-size: inherit !important;
    font-weight: inherit !important;
  }
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const DirectionValue = styled.span`
  * {
    color: inherit !important;
  }
`;
const baseStatisticStyles = css<{ $isBordered?: boolean }>`
  padding: 0 20px;
  flex: 1;
  margin-bottom: 16px;
  ${(p) =>
    p.$isBordered &&
    `
    border-right: ${p.theme.borders.light};
  `}
  ${(p) =>
    p.theme.isNewLook &&
    css`
      align-items: flex-start;
      gap: 4px;
      margin-bottom: 0;
      padding: 14px 20px;
      min-width: 0;
      border-right: none;
      box-shadow: -1px 0 0 ${getNewLookTokens(p.theme).hairline},
        0 -1px 0 ${getNewLookTokens(p.theme).hairline};
      h4.ant-typography,
      h5.ant-typography {
        margin: 0;
        ${newLookType.label};
        color: ${getNewLookTokens(p.theme).secondary};
      }
      > div:last-child {
        ${newLookType.figureLarge};
        color: ${getNewLookTokens(p.theme).ink};
        * {
          font-size: inherit;
          font-weight: inherit;
        }
      }
    `}
`;
const StyledStatistic = styled(Statistic)<{ $isBordered?: boolean }>`
  ${baseStatisticStyles}
`;
const StyledPriceStatistic = styled(PriceStatistic)<{ $isBordered?: boolean }>`
  ${baseStatisticStyles};
`;
